import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Card, Form, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import {
  askQuestion,
  requestWidgetHandover,
  addBotMessage,
  addExternalMessage,
  fetchWidgetHandoverStatus,
} from '../features/chatSlice';
import { submitChatFeedback } from '../features/logSlice';
import EmailPrompt from '../components/EmailPrompt';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import '../styles/ai-theme.css';
import useSiteBootstrap from '../hooks/useSiteBootstrap';

const useStreamingEffect = (text, onUpdate, cps = 60) => {
  const idx = useRef(0);
  const last = useRef(0);
  const rate = cps / 1000;

  useEffect(() => {
    if (!text) return undefined;

    idx.current = 0;
    last.current = 0;

    let raf;

    const tick = (ts) => {
      if (!last.current) last.current = ts;

      const delta = ts - last.current;
      last.current = ts;

      const step = Math.max(1, Math.floor(delta * rate * 8));
      idx.current = Math.min(text.length, idx.current + step);

      onUpdate(text.slice(0, idx.current));

      if (idx.current < text.length) {
        raf = requestAnimationFrame(tick);
      }
    };

    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [text, onUpdate, rate]);
};

const useAutoResize = (ref, max = 160) => {
  const resize = useCallback(() => {
    if (!ref.current) return;

    ref.current.style.height = 'auto';
    ref.current.style.height = `${Math.min(ref.current.scrollHeight, max)}px`;
  }, [ref, max]);

  return resize;
};

const copyText = async (t) => {
  try {
    await navigator.clipboard.writeText(t);
    return true;
  } catch {
    return false;
  }
};

const Avatar = React.memo(function Avatar({ type }) {
  const isBot = type === 'bot';

  const base = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
    flex: '0 0 40px',
    userSelect: 'none',
    transform: 'translateZ(0)',
    transition: 'transform .2s ease',
  };

  const style = {
    ...base,
    background: isBot
      ? 'linear-gradient(135deg,#0ea5e9 0%,#3b82f6 100%)'
      : 'linear-gradient(135deg,#64748b 0%,#0f172a 100%)',
  };

  const title = isBot ? 'AI Agent' : 'You';

  return (
    <div
      className={`avatar ${isBot ? 'avatar-bot' : 'avatar-user'}`}
      style={style}
      title={title}
      aria-label={title}
    >
      {isBot ? (
        <svg width="22" height="22" viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path
            fill="white"
            d="M11 2h2v2.1a7.96 7.96 0 0 1 6 7.9v5a3 3 0 0 1-3 3h-8a3 3 0 0 1-3-3v-5A7.96 7.96 0 0 1 11 4.1V2Zm-4 10h2v2H7v-2Zm8 0h2v2h-2v-2ZM9 18h6a1 1 0 0 0 1-1v-1H8v1a1 1 0 0 0 1 1Z"
          />
        </svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" role="img" aria-hidden="true">
          <path
            fill="white"
            d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5Z"
          />
        </svg>
      )}
    </div>
  );
});

const TypingIndicator = React.memo(function TypingIndicator({ label = 'Typing' }) {
  return (
    <div className="d-flex align-items-start gap-2 mb-3 justify-content-start">
      <Avatar type="bot" />

      <div className="msg-wrap position-relative">
        <div
          className="msg msg-bot"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            minHeight: 42,
          }}
        >
          <span>{label}</span>

          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              gap: 4,
              alignItems: 'center',
            }}
          >
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </span>
        </div>
      </div>
    </div>
  );
});

const MessageItem = React.memo(function MessageItem({ msg, isLast, onFeedback, onHandover }) {
  const [stream, setStream] = useState('');
  const [copied, setCopied] = useState(false);

  const plugins = useMemo(() => [remarkGfm], []);
  const rehype = useMemo(() => (msg.sender === 'bot' ? [rehypeHighlight] : []), [msg.sender]);
  const showStream = isLast && msg.sender === 'bot' && msg.fullText;

  useStreamingEffect(showStream ? msg.fullText : '', setStream, 60);

  const raw = showStream ? stream : msg.fullText || msg.text || '';
  const isUser = msg.sender === 'user';

  const onCopy = async () => {
    const ok = await copyText(typeof raw === 'string' ? raw : String(raw));
    setCopied(ok);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div className={`d-flex align-items-start gap-2 mb-3 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
      {!isUser && <Avatar type="bot" />}

      <div className="msg-wrap position-relative">
        <div className={`msg ${isUser ? 'msg-user' : 'msg-bot'}`}>
          <ReactMarkdown remarkPlugins={plugins} rehypePlugins={rehype}>
            {raw}
          </ReactMarkdown>
        </div>

        <button
          type="button"
          aria-label="Copy message"
          className={`copy-btn ${copied ? 'copied' : ''}`}
          onClick={onCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>

        {!isUser && msg.chat_id ? (
          <div className="d-flex gap-2 mt-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => onFeedback?.(msg.chat_id, 'positive')}
            >
              <span aria-hidden="true" className="me-1">👍</span>
              Helpful
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => onFeedback?.(msg.chat_id, 'negative')}
            >
              <span aria-hidden="true" className="me-1">👎</span>
              Not helpful
            </button>

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => onHandover?.()}
            >
              Talk to human
            </button>
          </div>
        ) : null}
      </div>

      {isUser && <Avatar type="user" />}
    </div>
  );
});

const SuggestionBar = React.memo(function SuggestionBar({ onPick, items }) {
  return (
    <div className="sugg-bar" role="list" aria-label="Suggested prompts">
      {items.map((it, i) => (
        <button
          key={i}
          type="button"
          role="listitem"
          className="sugg-chip"
          onClick={() => onPick(it.fill)}
          title={it.label}
          aria-label={it.label}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
});

function ChatPageSkeleton() {
  return (
    <div className="ai-scope">
      <div className="ai-bg">
        <Container className="page-pad" style={{ maxWidth: 960, position: 'relative', zIndex: 1 }}>
          <Card className="glass-card p-0 chat-card" aria-busy="true" aria-live="polite">
            <div className="glass-header chat-header">
              <div className="badge-row">
                <div className="badge-soft placeholder-glow">
                  <span className="placeholder col-12" />
                </div>
                <div className="badge-soft placeholder-glow">
                  <span className="placeholder col-12" />
                </div>
                <div className="badge-soft placeholder-glow">
                  <span className="placeholder col-12" />
                </div>
              </div>

              <div className="user-chip placeholder-glow" style={{ minWidth: 120 }}>
                <span className="placeholder col-12" />
              </div>
            </div>

            <div className="chat-body">
              <div className="chat-wrap" style={{ '--composer-h': '72px' }}>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="d-flex align-items-start gap-2 mb-3">
                    <div
                      className="avatar"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: '#334155',
                      }}
                    />
                    <div className="msg-wrap w-100">
                      <div className="msg msg-bot placeholder-glow">
                        <span className="placeholder col-10 mb-2" />
                        <span className="placeholder col-8 mb-2" />
                        <span className="placeholder col-6" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="composer-sticky d-flex flex-column flex-sm-row align-items-stretch gap-2">
                <div className="input-ai chat-input placeholder-glow" style={{ minHeight: 44 }}>
                  <span className="placeholder col-12" />
                </div>
                <Button className="btn-ai send-btn" disabled>
                  Send
                </Button>
              </div>
            </div>
          </Card>
        </Container>
      </div>
    </div>
  );
}

function ChatPage() {
  const dispatch = useDispatch();

  const {
    history,
    email,
    guest_name,
    loading,
    active_thread_id: activeThreadId,
    active_agent_name: activeAgentName,
  } = useSelector((s) => s.chat);

  const bootstrap = useSiteBootstrap();
  const chatUi = bootstrap?.chat_ui_settings || {};
  const siteSettings = bootstrap?.settings || {};

  const suggestions = Array.isArray(chatUi?.suggestions)
    ? chatUi.suggestions.map((s) => ({ label: s, fill: s }))
    : [];

  const inputPlaceholder = String(chatUi?.input_placeholder || '').trim();

  const uiConfigErrors = useMemo(() => {
    const missing = [];

    if (!inputPlaceholder) missing.push('Input placeholder');
    if (!String(chatUi?.handover_request_text || '').trim()) {
      missing.push('Handover request trigger message');
    }

    return missing;
  }, [chatUi, inputPlaceholder]);

  const widgetToken = useMemo(() => {
    const qp = new URLSearchParams(window.location.search);
    return (qp.get('site_token') || qp.get('widget_token') || '').trim();
  }, []);

  const [question, setQuestion] = useState('');
  const [pendingUserMessage, setPendingUserMessage] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [activeHandoverId, setActiveHandoverId] = useState(null);
  const [activeHandoverStatus, setActiveHandoverStatus] = useState('');
  const [lastAnnouncedAgent, setLastAnnouncedAgent] = useState('');
  const [currentAgentName, setCurrentAgentName] = useState(activeAgentName || '');

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const composerRef = useRef(null);
  const sendingRef = useRef(false);

  const autoResize = useAutoResize(inputRef, 160);
  const [composerH, setComposerH] = useState(72);
  const isBootstrapReady = Boolean(bootstrap);

  const renderedHistory = useMemo(() => {
    if (!pendingUserMessage) return history;

    return [
      ...history,
      {
        sender: 'user',
        text: pendingUserMessage,
        pending: true,
      },
    ];
  }, [history, pendingUserMessage]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    const el = listRef.current;

    if (!el) return;

    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior,
      });
    });
  }, []);

  useEffect(() => {
    if (activeAgentName) {
      setCurrentAgentName(activeAgentName);
    }
  }, [activeAgentName]);

  useEffect(() => {
    if (!email) {
      setShowPrompt(true);
    }
  }, [email]);

  useEffect(() => {
    const el = composerRef.current;

    if (!el) return undefined;

    const ro = new ResizeObserver((entries) => {
      const h = Math.ceil(entries[0].contentRect.height);
      setComposerH(h);
    });

    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    scrollToBottom('auto');
  }, [renderedHistory.length, loading, scrollToBottom]);

  useEffect(() => {
    const el = listRef.current;

    if (!el) return undefined;

    const toBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    const mo = new MutationObserver(() => requestAnimationFrame(toBottom));
    mo.observe(el, { childList: true, subtree: true, characterData: true });

    const ro = new ResizeObserver(() => requestAnimationFrame(toBottom));
    ro.observe(el);

    window.addEventListener('resize', toBottom);

    return () => {
      mo.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', toBottom);
    };
  }, []);

  useEffect(() => {
    const el = listRef.current;

    if (!el) return undefined;

    const onScroll = () => {
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollBtn(gap > 120);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (loading || pendingUserMessage) {
      scrollToBottom('auto');
    }
  }, [loading, pendingUserMessage, scrollToBottom]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (sendingRef.current) return;

    const q = question.trim();

    if (!q || loading) return;

    sendingRef.current = true;

    try {
      if (uiConfigErrors.length) {
        setActionMsg(`Chat UI is not configured in Admin AI Settings: ${uiConfigErrors.join(', ')}`);
        return;
      }

      setPendingUserMessage(q);
      setQuestion('');

      requestAnimationFrame(() => {
        scrollToBottom('auto');
      });

      const result = await dispatch(askQuestion({
        question: q,
        site_token: widgetToken,
        thread_id: activeThreadId,
      }));

      if (askQuestion.fulfilled.match(result) && result.payload?.handover_id) {
        setActiveHandoverId(result.payload.handover_id);
        setActiveHandoverStatus(result.payload?.handover_status || 'pending');

        const agentName = String(result.payload?.agent_name || '').trim();

        if (agentName) {
          setCurrentAgentName(agentName);
        }
      }

      inputRef.current?.focus();
      scrollToBottom('auto');
    } finally {
      setPendingUserMessage('');
      sendingRef.current = false;
    }
  }, [
    activeThreadId,
    dispatch,
    loading,
    question,
    scrollToBottom,
    uiConfigErrors,
    widgetToken,
  ]);

  const onKeyDown = (e) => {
    const metaEnter = (e.ctrlKey || e.metaKey) && e.key === 'Enter';
    const plainEnter = e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey;

    if (metaEnter || plainEnter) {
      e.preventDefault();
      onSubmit(e);
    }
  };

  const onPickSuggestion = (text) => {
    setQuestion(text);
    inputRef.current?.focus();
  };

  const handleFeedback = async (chatId, value) => {
    const result = await dispatch(submitChatFeedback({
      chat_id: chatId,
      value,
      widget_token: widgetToken,
    }));

    if (submitChatFeedback.fulfilled.match(result)) {
      setActionMsg('Thanks for your feedback.');
    } else {
      setActionMsg(result.payload || 'Failed to submit feedback.');
    }
  };

  const handleHandover = async () => {
    const handoverRequestText = String(chatUi?.handover_request_text || '').trim();

    if (!handoverRequestText) {
      setActionMsg('Handover request text is not configured in Admin AI Settings.');
      return;
    }

    const result = await dispatch(requestWidgetHandover({
      message: handoverRequestText,
      email,
      name: guest_name,
      thread_id: activeThreadId,
      site_token: widgetToken,
    }));

    if (requestWidgetHandover.fulfilled.match(result)) {
      const msg = String(result.payload?.message || 'Handover requested.').trim();
      const handover = result.payload?.handover || {};
      const agentName = String(handover?.picked_by_name || result.payload?.agent_name || '').trim();

      if (msg) {
        dispatch(addBotMessage(msg));
      }

      if (agentName) {
        setCurrentAgentName(agentName);
      }

      setActiveHandoverId(handover?.id || null);
      setActiveHandoverStatus(handover?.status || 'pending');
      setLastAnnouncedAgent(agentName || '');
      setActionMsg('');
      scrollToBottom('auto');
    } else {
      setActionMsg(result.payload || 'Could not request handover.');
    }
  };

  useEffect(() => {
    if (!activeHandoverId || activeHandoverStatus === 'resolved') return undefined;

    const poll = async () => {
      const result = await dispatch(fetchWidgetHandoverStatus({
        handover_id: activeHandoverId,
        thread_id: activeThreadId,
        site_token: widgetToken,
      }));

      if (!fetchWidgetHandoverStatus.fulfilled.match(result)) return;

      const status = result.payload?.status || '';
      const agentName = String(result.payload?.agent_name || '').trim();
      const resolvedMessage = String(result.payload?.message || '').trim();
      const messages = Array.isArray(result.payload?.messages) ? result.payload.messages : [];

      if (agentName) {
        setCurrentAgentName(agentName);
      }

      messages
        .filter((m) => m.role === 'agent')
        .forEach((m) => {
          dispatch(addExternalMessage({
            id: m.id,
            text: m.text,
            sender: 'bot',
            role: 'agent',
            created_at: m.created_at,
          }));
        });

      if (status === 'resolved' && activeHandoverStatus !== 'resolved' && resolvedMessage) {
        dispatch(addBotMessage(resolvedMessage));
      }

      setActiveHandoverStatus(status);

      if ((status === 'picked' || status === 'active') && agentName && agentName !== lastAnnouncedAgent) {
        dispatch(addBotMessage(`${agentName} has joined this conversation and will continue assisting you from here.`));
        setLastAnnouncedAgent(agentName);
      }

      scrollToBottom('auto');
    };

    poll();

    const id = setInterval(poll, 5000);

    return () => clearInterval(id);
  }, [
    activeHandoverId,
    activeHandoverStatus,
    activeThreadId,
    dispatch,
    lastAnnouncedAgent,
    scrollToBottom,
    widgetToken,
  ]);

  if (!isBootstrapReady) {
    return <ChatPageSkeleton />;
  }

  const seoTitle = String(siteSettings?.seo_title || siteSettings?.site_name || '').trim();
  const seoDescription = String(siteSettings?.seo_description || '').trim();
  const seoKeywords = String(siteSettings?.seo_keywords || '').trim();
  const favicon = String(siteSettings?.favicon_url || '').trim();

  const activeHumanSupport = activeHandoverId && ['picked', 'active'].includes(activeHandoverStatus);
  const typingLabel = activeHumanSupport
    ? `${currentAgentName || 'Human agent'} is typing`
    : 'AI Agent is typing';

  return (
    <div className="ai-scope">
      <Helmet>
        {seoTitle ? <title>{seoTitle}</title> : null}
        {seoDescription ? <meta name="description" content={seoDescription} /> : null}
        {seoKeywords ? <meta name="keywords" content={seoKeywords} /> : null}
        {seoTitle ? <meta property="og:title" content={seoTitle} /> : null}
        {seoDescription ? <meta property="og:description" content={seoDescription} /> : null}
        {seoTitle ? <meta name="twitter:title" content={seoTitle} /> : null}
        {seoDescription ? <meta name="twitter:description" content={seoDescription} /> : null}
        {favicon ? <link rel="icon" href={favicon} /> : null}
      </Helmet>

      <div className="ai-bg">
        {actionMsg ? (
          <Container className="pt-2">
            <Card className="p-2 small">{actionMsg}</Card>
          </Container>
        ) : null}

        {uiConfigErrors.length ? (
          <Container className="pt-2">
            <Card className="p-2 small text-danger">
              Chat UI setup required: {uiConfigErrors.join(', ')}.
            </Card>
          </Container>
        ) : null}

        <Container className="page-pad" style={{ maxWidth: 960, position: 'relative', zIndex: 1 }}>
          <Card className="glass-card p-0 chat-card">
            <div className="glass-header chat-header">
              <div className="badge-row">
                <div className="badge-soft">
                  {activeHumanSupport ? 'Human Support' : 'AI Agent'}
                </div>

                <div className="badge-soft">Secure</div>
                <div className="badge-soft">Realtime</div>

                {activeHumanSupport ? (
                  <div className="badge-soft">
                    Agent: {currentAgentName || 'Joining...'}
                  </div>
                ) : null}
              </div>

              <div className="user-chip">
                User: {email || guest_name || 'Guest'}
              </div>
            </div>

            <div className="chat-body">
              {history.length === 0 && !pendingUserMessage && !loading && suggestions.length > 0 && (
                <SuggestionBar onPick={onPickSuggestion} items={suggestions} />
              )}

              <div
                ref={listRef}
                className="chat-wrap"
                aria-live="polite"
                aria-busy={loading ? 'true' : 'false'}
                style={{
                  '--composer-h': `${composerH}px`,
                }}
              >
                {renderedHistory.map((msg, i) => (
                  <MessageItem
                    key={`${msg.sender || 'msg'}-${msg.external_id || msg.chat_id || msg.pending || i}`}
                    msg={msg}
                    isLast={i === renderedHistory.length - 1}
                    onFeedback={handleFeedback}
                    onHandover={handleHandover}
                  />
                ))}

                {loading && (
                  <TypingIndicator label={typingLabel} />
                )}
              </div>

              <Form
                ref={composerRef}
                onSubmit={onSubmit}
                className="composer-sticky d-flex flex-column flex-sm-row align-items-stretch gap-2"
              >
                <Form.Control
                  ref={inputRef}
                  className="input-ai chat-input"
                  as="textarea"
                  rows={1}
                  onInput={autoResize}
                  onKeyDown={onKeyDown}
                  placeholder={inputPlaceholder || 'Configure input placeholder in Admin AI Settings'}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                />

                <Button
                  type="submit"
                  className="btn-ai send-btn"
                  disabled={loading || !question.trim()}
                >
                  Send
                </Button>
              </Form>
            </div>
          </Card>

          {showScrollBtn && (
            <button
              type="button"
              className="scroll-bottom-btn"
              aria-label="Scroll to latest"
              onClick={() => scrollToBottom('smooth')}
              title="Jump to latest"
            >
              ↓
            </button>
          )}
        </Container>

        <EmailPrompt show={showPrompt} onClose={() => setShowPrompt(false)} />
      </div>
    </div>
  );
}

export default ChatPage;